import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
	findMemberByPfNumber,
	updateMember,
	getRegions,
	getRegionName,
	getRegionConst,
	getConstName,
	getConstBranches,
	getBranchName,
	getTitles,
} from "../../../api/apiServices";

import { format, sub } from "date-fns";

const UpdateProfile = ({ member }) => {
	// const id = parseInt(member.id)
	const pfNumber = member.pfNumber;

	const [regions, setRegions] = useState([]);
	const [constituencies, setConstituencies] = useState([]);
	const [branches, setBranches] = useState([]);
	const [titles, setTitles] = useState([]);

	const [title, setTitle] = useState("");
	const [last_name, setLastName] = useState("");
	const [first_name, setFirstName] = useState("");
	const [middle_name, setMiddleName] = useState("");

	const [date_of_birth, setDOB] = useState("");
	const [mobile_number, setMobile] = useState("");
	const [email, setEmail] = useState("");

	const [gender, setGender] = useState("");
	const [marital_status, setMaritalStatus] = useState("");

	const [region_code, setRegionCode] = useState("");
	const [region, setRegion] = useState("");
	const [constituency_code, setConstituencyCode] = useState("");
	const [constituency, setConstituency] = useState("");
	const [branch_code, setBranchCode] = useState("");
	const [branch, setBranch] = useState("");

	const [last_tertiary_institution, setLastInstitution] = useState("");
	const [qualification_type, setQualificationType] = useState("");
	const [qualification, setQualification] = useState("");
	const [profession, setProfesiion] = useState("");
	const [occupations, setOccupations] = useState("");
	const [employment_type, setEmploymentType] = useState("");
	const [current_employment, setCurrentEmployment] = useState("");

	const queryClient = useQueryClient();

	useEffect(() => {
		getRegions().then(setRegions);
		getTitles().then(setTitles);
	}, []);

	const navigate = useNavigate();
	const location = useLocation();
	const from = location.state?.from?.pathname || "/update_profile";

	const { data: memberUpd } = useQuery(["memberUpd", pfNumber], () =>
		findMemberByPfNumber(pfNumber)
	);

	const id = memberUpd?.member?.id;
	const memberInfo = memberUpd?.member;

	const updateMemberMutation = useMutation(updateMember, {
		onSuccess: () => {
			// Invalidate cached data
			queryClient.invalidateQueries("memberUpd");
			toast.success("Member information successfully updated", {
				duration: 5000,
				className: "toast-message",
			});
			navigate(from, { replace: true });
		},
		onError: async (err) => {
			toast.error(err.message, {
				duration: 5000,
				className: "toast-message",
			});
			return false;
		},
	});

	const handleDOB = (e) => {
		const minDate = sub(new Date(), {
			months: 216,
		});
		const dateFormat = "yyyy-MM-dd";
		const dateOfBirth = e.target.value;
		const memDOB = new Date(e.target.value);
		const today = new Date();
		const distance = today.getTime() - memDOB.getTime();
		const daysOld = Math.floor(distance / (1000 * 60 * 60 * 24));
		const memAge = Number((daysOld / 365).toFixed(0));
		if (memAge < 18) {
			toast.error(
				`The acceptable date of birth limit is ${format(
					minDate,
					dateFormat
				)} and below`
			);
			setDOB(dateOfBirth);
			return false;
		}
		setDOB(dateOfBirth);
		return true;
	};

	const handleLastname = (e) => {
		const lastName = e.target.value.replace(/[^A-Za-z-" "]/gi, "");
		setLastName(lastName);
	};
	const handleFirstname = (e) => {
		const firstName = e.target.value.replace(/[^A-Za-z-" "]/gi, "");
		setFirstName(firstName);
	};
	const handleMiddleName = (e) => {
		const middleName = e.target.value.replace(/[^A-Za-z-" "]/gi, "");
		setMiddleName(middleName);
	};

	const handleRegion = (e) => {
		const regCode = e.target.value;
		setRegionCode(regCode);
		getRegionName(regCode).then((res) => setRegion(res));
		getRegionConst(regCode).then(setConstituencies);
	};

	const handleConstituency = (e) => {
		const constCode = e.target.value;
		setConstituencyCode(constCode);
		getConstName(constCode).then((res) => setConstituency(res));
		getConstBranches(constCode).then(setBranches);
	};

	const handleBranch = (e) => {
		const branchCode = e.target.value;
		setBranchCode(branchCode);
		getBranchName(branchCode).then((res) => setBranch(res));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const data = {
			id: id,
			title: title,
			last_name: last_name,
			first_name: first_name,
			middle_name: middle_name,
			date_of_birth: date_of_birth,
			gender: gender,
			marital_status: marital_status,
			mobile_number: mobile_number,
			email: email,
			region_code: region_code,
			region: region,
			constituency_code: constituency_code,
			constituency: constituency,
			branch_code: branch_code,
			branch: branch,
			last_tertiary_institution: last_tertiary_institution,
			qualification_type: qualification_type,
			qualification: qualification,
			profession: profession,
			occupations: occupations,
			employment_type: employment_type,
			current_employment: current_employment,
		};
		updateMemberMutation.mutate(data);
	};

	const handleCancel = () => {
		navigate("/home");
	};

	const updateForm = (
		<>
			<form onSubmit={handleSubmit}>
				<fieldset>
					<legend>Personal Information</legend>
				</fieldset>
				<div className="form-group row">
					<label htmlFor="title" className="col-sm-2 label-name">
						Title<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<select
							type="text"
							id="title"
							autoComplete="off"
							onChange={(e) => setTitle(e.target.value)}
							defaultValue={memberInfo?.title}
							style={{ textTransform: "uppercase" }}
							name="title"
							className="form-control"
						>
							<option value="Select Title">Select Title</option>
							{titles.map((item, index) => (
								<option value={item.title} key={index}>
									{item.title}
								</option>
							))}
						</select>
					</div>
					<label htmlFor="first_name" className="col-sm-2 label-name">
						First Name<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="first_name"
							placeholder="First Name"
							autoComplete="off"
							onChange={handleFirstname}
							defaultValue={memberInfo?.first_name}
							style={{ textTransform: "uppercase" }}
							name="first_name"
							className="form-control"
						/>
					</div>
				</div>
				<div className="form-group row">
					<label
						htmlFor="middle_name"
						className="col-sm-2 label-name"
					>
						Middle Name
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="middle_name"
							placeholder="Middle Name"
							autoComplete="off"
							onChange={handleMiddleName}
							defaultValue={memberInfo?.middle_name}
							style={{ textTransform: "uppercase" }}
							name="middle_name"
							className="form-control"
						/>
					</div>
					<label htmlFor="last_name" className="col-sm-2 label-name">
						Last Name<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="last_name"
							autoComplete="off"
							placeholder="Last Name"
							onChange={handleLastname}
							defaultValue={memberInfo?.last_name}
							style={{ textTransform: "uppercase" }}
							name="last_name"
							className="form-control"
						/>
					</div>
				</div>
				<div className="form-group row">
					<label
						htmlFor="date_of_birth"
						className="col-sm-2 label-name"
					>
						Date of Birth<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="date"
							id="date_of_birth"
							autoComplete="off"
							onChange={(e) => setDOB(e.target.value)}
							onBlur={handleDOB}
							readOnly
							defaultValue={memberInfo?.date_of_birth}
							name="date_of_birth"
							className="form-control"
						/>
					</div>
					<label htmlFor="gender" className="col-sm-2 label-name">
						Gender<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="gender"
							autoComplete="off"
							style={{ textTransform: "uppercase" }}
							onChange={(e) => setGender(e.target.value)}
							readOnly
							defaultValue={memberInfo?.gender}
							name="gender"
							className="form-control"
						/>
					</div>
				</div>
				<div className="form-group row">
					<label
						htmlFor="marital_status"
						className="col-sm-2 label-name"
					>
						Marital Status<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<select
							type="text"
							id="marital_status"
							autoComplete="off"
							onChange={(e) => setMaritalStatus(e.target.value)}
							defaultValue={memberInfo?.marital_status}
							style={{ textTransform: "uppercase" }}
							name="marital_status"
							className="form-control"
						>
							<option value="Select Marital Status">
								Select Marital Status
							</option>
							<option value="Single">Single</option>
							<option value="Married">Married</option>
							<option value="Divorced">Divorced</option>
							<option value="Separated">Separated</option>
							<option value="Widowed">Widowed</option>
							<option value="Prefer Not to Say">
								Prefer Not to Say
							</option>
						</select>
					</div>
					<label className="col-sm-2 label-name">
						Mobile Number<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="number"
							id="mobile_number"
							name="mobile_number"
							defaultValue={memberInfo?.mobile_number}
							onChange={(e) => setMobile(e.target.value)}
							className="form-control"
						/>
					</div>
				</div>
				<div className="form-group row">
					<label htmlFor="email" className="col-sm-2 label-name">
						Email Address<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="email"
							id="email"
							placeholder="Email Address"
							autoComplete="off"
							onChange={(e) => setEmail(e.target.value)}
							style={{ fontSize: "16px" }}
							defaultValue={memberInfo?.email}
							name="email"
							className="form-control"
						/>
					</div>
				</div>
				<fieldset>
					<legend>Region & Constituency</legend>
				</fieldset>
				<div className="form-group row">
					<label htmlFor="region" className="col-sm-2 label-name">
						Current Region<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="region"
							autoComplete="off"
							readOnly
							style={{ textTransform: "uppercase" }}
							value={memberInfo?.region}
							className="form-control"
						/>
					</div>
					<label htmlFor="region" className="col-sm-2 label-name">
						Region<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<select
							type="text"
							id="region"
							autoComplete="off"
							onChange={handleRegion}
							style={{ textTransform: "uppercase" }}
							defaultValue={memberInfo?.region_code}
							name="region"
							className="form-control"
						>
							<option value="Select Region">Select Region</option>
							{regions.map((reg, index) => (
								<option value={reg.region_code} key={index}>
									{reg.region} - {reg.region_code}
								</option>
							))}
						</select>
					</div>
				</div>

				<div className="form-group row">
					<label
						htmlFor="constituency"
						className="col-sm-2 label-name"
					>
						Current Constituency<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="constituency"
							autoComplete="off"
							readOnly
							style={{ textTransform: "uppercase" }}
							value={memberInfo?.constituency}
							className="form-control"
						/>
					</div>
					<label
						htmlFor="constituency"
						className="col-sm-2 label-name"
					>
						Constituency<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<select
							type="text"
							id="constituency"
							autoComplete="off"
							onChange={handleConstituency}
							style={{ textTransform: "uppercase" }}
							defaultValue={memberInfo?.constituency_code}
							name="constituency"
							className="form-control"
						>
							<option value="Select Constituency<">
								Select Constituency
							</option>
							{constituencies.map((consti, index) => (
								<option
									value={consti.constituency_code}
									key={index}
								>
									{consti.constituency} -{" "}
									{consti.constituency_code}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="form-group row">
					<label htmlFor="branch" className="col-sm-2 label-name">
						Current Branch<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="branch"
							autoComplete="off"
							style={{ textTransform: "uppercase" }}
							readOnly
							value={memberInfo?.branch}
							className="form-control"
						/>
					</div>
					<label htmlFor="branch" className="col-sm-2 label-name">
						Branch<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<select
							type="text"
							id="branch"
							autoComplete="off"
							style={{ textTransform: "uppercase" }}
							onChange={handleBranch}
							defaultValue={memberInfo?.branch}
							name="branch"
							className="form-control"
						>
							<option value="Select Branch">Select Branch</option>
							{branches.map((brnch, index) => (
								<option value={brnch.branch_code} key={index}>
									{brnch.branch} - {brnch.branch_code}
								</option>
							))}
						</select>
					</div>
				</div>

				<fieldset>
					<legend>Education Background</legend>
				</fieldset>
				<div className="form-group row">
					<label
						htmlFor="last_tertiary_institution"
						className="col-sm-2 label-name"
					>
						Current Tertiary Institution<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="last_tertiary_institution"
							autoComplete="off"
							style={{ textTransform: "uppercase" }}
							value={memberInfo?.last_tertiary_institution}
							readOnly
							className="form-control"
						/>
					</div>
					<label
						htmlFor="last_tertiary_institution"
						className="col-sm-2 label-name"
					>
						Update Tertiary Institution<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="last_tertiary_institution"
							placeholder="Last Tertiary Institution Attended"
							autoComplete="off"
							style={{ textTransform: "uppercase" }}
							onChange={(e) => setLastInstitution(e.target.value)}
							defaultValue={memberInfo?.last_tertiary_institution}
							name="last_tertiary_institution"
							className="form-control"
						/>
					</div>
				</div>
				<div className="form-group row">
					<label
						htmlFor="qualification_type"
						className="col-sm-2 label-name"
					>
						{" "}
						Current Qualification<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="qualification_type"
							autoComplete="off"
							readOnly
							value={memberInfo?.qualification_type}
							style={{ textTransform: "uppercase" }}
							className="form-control"
						/>
					</div>
					<label
						htmlFor="qualification_type"
						className="col-sm-2 label-name"
					>
						Update Qualification<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<select
							type="text"
							id="qualification_type"
							autoComplete="off"
							onChange={(e) =>
								setQualificationType(e.target.value)
							}
							defaultValue={memberInfo?.qualification_type}
							style={{ textTransform: "uppercase" }}
							name="qualification_type"
							className="form-control"
						>
							<option value="">Select Option</option>
							<option value="1st Degree">1st Degree</option>
							<option value="2nd Degree">2nd Degree</option>
							<option value="3rd Degree">3rd Degree</option>
							<option value="Others">Others</option>
						</select>
					</div>
				</div>
				<div className="form-group row">
					<label
						htmlFor="qualification"
						className="col-sm-2 label-name"
					>
						Current Qualification<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="qualification"
							autoComplete="off"
							style={{ textTransform: "uppercase" }}
							value={memberInfo?.qualification}
							readOnly
							name="qualification"
							className="form-control"
						/>
					</div>
					<label
						htmlFor="qualification"
						className="col-sm-2 label-name"
					>
						Update Qualification<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="qualification"
							autoComplete="off"
							placeholder="Indicate Qualification"
							style={{ textTransform: "uppercase" }}
							onChange={(e) => setQualification(e.target.value)}
							defaultValue={memberInfo?.qualification}
							name="qualification"
							className="form-control"
						/>
					</div>
				</div>
				<fieldset>
					<legend>Profession</legend>
				</fieldset>
				<div className="form-group row">
					<label htmlFor="profession" className="col-sm-2 label-name">
						Current Profession<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="profession"
							autoComplete="off"
							placeholder="Profession"
							style={{ textTransform: "capitalize" }}
							onChange={(e) => setProfesiion(e.target.value)}
							defaultValue={memberInfo?.profession}
							name="profession"
							className="form-control"
						/>
					</div>
					<label
						htmlFor="occupations"
						className="col-sm-2 label-name"
					>
						Occupations<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="occupations"
							placeholder="Occupations"
							autoComplete="off"
							style={{ textTransform: "capitalize" }}
							onChange={(e) => setOccupations(e.target.value)}
							defaultValue={memberInfo?.occupations}
							name="occupations"
							className="form-control"
						/>
					</div>
				</div>
				<div className="form-group row">
					<label
						htmlFor="employment_type"
						className="col-sm-2 label-name"
					>
						Employment Type<span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<select
							type="text"
							id="employment_type"
							autoComplete="off"
							onChange={(e) => setEmploymentType(e.target.value)}
							defaultValue={memberInfo?.employment_type}
							style={{ textTransform: "uppercase" }}
							name="employment_type"
							className="form-control"
						>
							<option value="Select Option">Select Option</option>
							<option value="Formal">Formal</option>
							<option value="Informal">Informal</option>
						</select>
					</div>
					<label
						htmlFor="current_employment"
						className="col-sm-2 label-name"
					>
						Current Employment <span>&nbsp;&nbsp;*</span>
					</label>
					<div className="col-sm-4">
						<input
							type="text"
							id="current_employment"
							autoComplete="off"
							placeholder="Current Employment"
							style={{ textTransform: "uppercase" }}
							onChange={(e) =>
								setCurrentEmployment(e.target.value)
							}
							defaultValue={memberInfo?.current_employment}
							name="current_employment"
							className="form-control"
						/>
					</div>
				</div>
				<div className="panel-footer">
					<div className="form-group row">
						<div className="col-sm-2">
							<button
								type="button"
								className="btn btn-danger"
								onClick={handleCancel}
							>
								Cancel
							</button>
							&nbsp;&nbsp;&nbsp;&nbsp;
							<button type="submit" className="btn btn-success">
								Submit
							</button>
						</div>
					</div>
				</div>
			</form>
		</>
	);
	return (
		<>
			<section className="content-header">
				<Toaster toastOptions={{ position: "top-center" }} />
				<h1>Update Profile Request </h1>
			</section>
			<section className="content">
				<div className="box">
					<div className="box-body">
						<div className="panel panel-default panel-br-4">
							<div className="panel-heading">
								<div className="form-group row">
									<div className="col-sm-12">
										<h4 className="panel-title">
											<span>
												Request Member Profile Update
											</span>
										</h4>
									</div>
								</div>
							</div>
							<div className="panel-body">
								<fieldset className="col-md-12">
									{updateForm}
								</fieldset>
							</div>
						</div>
					</div>
				</div>
			</section>
		</>
	);
};
export default UpdateProfile;
